import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Search,
  Building,
  MapPin,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/GlobalStyles.css'
import { SUBJECT_TAXONOMY } from "../utils/SUBJECT_TAXONOMY";



const TeacherJobsPage = () => {
  const { location } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [appliedJobIds, setAppliedJobIds] = useState(new Set());

  /* ================= LOCATION FORMAT ================= */


  const formatLocation = useCallback((loc) => {
    if (!loc) return null;
    const locationMap = {
      bangalore: 'Bengaluru',
      hyderabad: 'Hyderabad',
      delhi: 'Delhi',
      mumbai: 'Mumbai',
      chennai: 'Chennai',
      kolkata: 'Kolkata',
      pune: 'Pune',
    };
    return locationMap[loc.toLowerCase()] || loc;
  }, []);


  /* ================= QUERY PARAMS ================= */

  const designationParam = searchParams.get("designation");
  const categoryParam = searchParams.get("category"); // teaching | non-teaching
  const domainParam = searchParams.get("domain");     // college | university


  /* ================= FILTER STATE ================= */

  const [selectedFilters, setSelectedFilters] = useState({
    location: location ? formatLocation(location) : null,
    experience: null,
    keyword: '',
    designation: null,     // math, physics, counsellor
    category: null,        // Teaching | Non Teaching
    domain: null,          // School | College | University | EdTech
  });

  useEffect(() => {
    setSelectedFilters(prev => ({
      ...prev,
      designation: designationParam || null,
      category: categoryParam || null,
      domain: domainParam || null,
      location: location ? formatLocation(location) : prev.location,
    }));
  }, [designationParam, categoryParam, domainParam, location, formatLocation]);



  const [allJobs, setAllJobs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  useEffect(() => {
    if (jobs.length > 0) {
      setSelectedJob(jobs[0]);
    }
  }, [jobs]);

  const normalizeText = useCallback((text = "") => {
    return text
      .toLowerCase()
      .replace(/[-_/]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }, []);




  const matchesDesignation = useCallback(
    (job, designationParam) => {
      if (!designationParam) {

        return true;
      }


      let designation = normalizeText(designationParam);


      designation = designation
        .replace(" teacher", "")
        .replace(" lecturer", "")
        .trim();


      /* ================= TEACHING ROLES ================= */
      if (designation === "teacher") {
        return job.category === "Teaching";
      }

      if (designation === "assistant teacher") {
        return job.role_teaching === "Assistant Teacher";
      }

      if (designation === "head teacher") {
        return job.role_teaching === "Head Teacher";
      }

      if (designation === "special educator") {
        return job.role_teaching === "Special Educator";
      }

      /* ================= NON-TEACHING ROLES ================= */
      if (designation === "academic counsellor") {
        return job.role_non_teaching === "Academic Counsellor";
      }

      if (designation === "academic coordinator") {
        return job.role_non_teaching === "Academic Coordinator";
      }

      /* ================= SUBJECT-BASED ================= */

      // Subject-based searches must return ONLY teaching jobs with subjects
      if (job.category !== "Teaching" || !job.subject) {
        return false;
      }

      const allowedSubjects =
        SUBJECT_TAXONOMY[designation] || [designation];

      return allowedSubjects.some(subject =>
        normalizeText(job.subject).includes(subject)
      );


    },
    [normalizeText]
  );


  const experienceYears = Array.from({ length: 9 }, (_, i) => i);

  /* ================= FETCH JOBS ================= */

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/jobs/search-jobs`,
          {
            params: {
              category: categoryParam, // Teaching | Non Teaching
              domain: domainParam,     // College | University
              location: location ? formatLocation(location) : null,
              minExp: selectedFilters.experience,
              keyword: selectedFilters.keyword,
            }

          }
        );

        setAllJobs(response.data || []);
      } catch (err) {
        console.error('Failed to fetch job listings.', err);
      }
    };

    fetchJobs();
  }, [
    designationParam,
    categoryParam,
    domainParam,
    location,
    selectedFilters.experience,
    selectedFilters.keyword,
    formatLocation
  ]);


  /* ================= FETCH LOCATIONS ================= */

  useEffect(() => {
    const defaultCities = [
      { name: 'Bengaluru' },
      { name: 'Hyderabad' },
      { name: 'Mumbai' },
      { name: 'Delhi' },
    ];

    const fetchMetaData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/user/allCity`,
          { withCredentials: true }
        );
        const formatted = Array.isArray(res.data)
          ? res.data.map((c) => (typeof c === 'string' ? { name: c } : c))
          : [];
        setLocations(formatted.length ? formatted : defaultCities);
      } catch {
        setLocations(defaultCities);
      }
    };

    fetchMetaData();
  }, []);

  const removeFilter = (key) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [key]: null,
    }));

    // 🔥 ALSO fix the URL
    if (key === "location") {
      navigate("/jobs" + window.location.search, { replace: true });
    }

    if (key === "category") {
      const params = new URLSearchParams(searchParams);
      params.delete("category");
      navigate(`/jobs${location ? `/${location}` : ""}?${params.toString()}`, {
        replace: true,
      });
    }

    if (key === "designation") {
      const params = new URLSearchParams(searchParams);
      params.delete("designation");
      navigate(`/jobs${location ? `/${location}` : ""}?${params.toString()}`, {
        replace: true,
      });
    }
    if (key === "domain") {
      const params = new URLSearchParams(searchParams);
      params.delete("domain");
      navigate(`/jobs${location ? `/${location}` : ""}?${params.toString()}`, {
        replace: true,
      });
    }

  };

  useEffect(() => {
    const fetchAppliedJobs = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/job/apply/allAppliedJobs`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );


        const ids = new Set(res.data.map(app => {
          const jId = app.job_id;
          if (!jId) return null;
          if (typeof jId === 'object' && jId._id) return jId._id.toString();
          return jId.toString();
        }).filter(Boolean));

        setAppliedJobIds(ids);
      } catch (err) {
        console.error("Failed to fetch applied jobs", err);
      }
    };

    fetchAppliedJobs();

    const handleFocus = () => {
      fetchAppliedJobs();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  const isJobActive = useCallback((job) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // If explicitly inactive
    if (job.is_active === false) return false;

    // If explicitly closed
    if (job.status === "closed") return false;

    // If closing date exists & is expired
    if (job.closing_date) {
      const closingDate = new Date(job.closing_date);
      closingDate.setHours(0, 0, 0, 0);
      if (closingDate < today) return false;
    }

    return true;
  }, []);

  /* ================= SYNC URL → STATE ================= */



  /* ================= FILTER LOGIC ================= */

  const filterJobs = useCallback(() => {
    const filtered = allJobs.filter((job) => {

      // 🔒 FILTER OUT EXPIRED / INACTIVE JOBS
      if (!isJobActive(job)) return false;


      /* LOCATION */
      const cityMatch = selectedFilters.location
        ? job.address?.city?.toLowerCase() ===
        selectedFilters.location.toLowerCase()
        : true;

      /* EXPERIENCE */
      const expMatch =
        selectedFilters.experience != null
          ? job.min_experience <= selectedFilters.experience &&
          job.max_experience >= selectedFilters.experience
          : true;

      /* KEYWORD */
      const keywordMatch = selectedFilters.keyword
        ? normalizeText(job.title).includes(
          normalizeText(selectedFilters.keyword)
        )
        : true;

      /* CATEGORY — Teaching / Non Teaching */
      const categoryMatch = selectedFilters.category
        ? normalizeText(job.category) === normalizeText(selectedFilters.category)
        : true;


      /* DOMAIN — School / College / University / EdTech */
      const domainMatch = selectedFilters.domain
        ? normalizeText(job.domain) === normalizeText(selectedFilters.domain)
        : true;



      /* DESIGNATION — subject based */
      const designationMatch = matchesDesignation(
        job,
        selectedFilters.designation
      );

      return (
        cityMatch &&
        expMatch &&
        keywordMatch &&
        categoryMatch &&
        domainMatch &&
        designationMatch
      );
    });

    setJobs(filtered);
  }, [
    allJobs,
    selectedFilters,
    normalizeText,
    matchesDesignation,
    isJobActive
  ]);





  /* ================= EFFECTS ================= */

  useEffect(() => {
    filterJobs();
    setSelectedJob(null);   // reset preview when job list changes
  }, [filterJobs]);



  /* ================= RESET ================= */

  const handleReset = () => {
    setSelectedFilters({
      location: null,
      experience: null,
      keyword: '',
      designation: null,
      category: null,
      domain: null,
    });
  };

  const formatPostedTime = (date) => {
    if (!date) return "Posted recently";

    const diffMs = Date.now() - new Date(date);
    const mins = Math.floor(diffMs / 60000);

    if (mins < 60) return `Posted ${mins} mins ago`;

    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Posted ${hours} hours ago`;

    const days = Math.floor(hours / 24);
    return `Posted ${days} days ago`;
  };


  /* ================= RENDER ================= */

  return (
    <div className="global-font">
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="h-[96px]" />
        {/* RESULT HEADER */}
        <section className="bg-white border-b">
          <div className="container px-4 py-6 mx-auto flex justify-between items-center ">
            <div>
              <p className="text-sm text-gray-500">Results For</p>
              <h1 className="text-xl font-semibold capitalize">
                {[
                  selectedFilters.designation,
                  selectedFilters.category,
                  selectedFilters.domain,
                  selectedFilters.location,
                ]
                  .filter(Boolean)
                  .map(v => v.replace(/-/g, " "))
                  .join(" • ") || "All Jobs"}
              </h1>


            </div>
            {/* <button className="text-sm text-blue-600">Modify Search</button> */}
          </div>
        </section>

        {/* SEARCH BAR */}
        <section className="bg-blue-50 py-4">
          <div className="container px-4 mx-auto">
            <div className="relative">
              <Search className="absolute w-5 h-5 text-gray-400 left-4 top-3" />
              <input
                type="text"
                placeholder={`Search jobs${selectedFilters.location ? ` near ${selectedFilters.location}` : ''}`}

                value={selectedFilters.keyword}
                onChange={(e) =>
                  setSelectedFilters((p) => ({ ...p, keyword: e.target.value }))
                }
                className="w-full py-3 pl-12 pr-4 text-sm border rounded-md"
              />
            </div>
          </div>
        </section>

        {/* MAIN */}
        <main className="container px-4 py-6 mx-auto">
          <div className="flex flex-col md:flex-row gap-6 jobs-page-layout">



            {/* FILTERS */}
            <aside className="hidden md:block md:w-1/4 job-filter-sidebar">


              <div className="p-5 bg-white rounded-xl shadow">
                <h3 className="mb-4 font-semibold">Filters</h3>

                <div className="mb-4">
                  {Object.entries(selectedFilters).map(
                    ([key, value]) =>
                      value && (
                        <span
                          key={key}
                          className="mr-2 mb-2 inline-flex items-center gap-1 px-3 py-1 text-xs bg-blue-100 rounded-full"
                        >
                          <span className="capitalize">
                            {key}: {value}
                          </span>

                          <button
                            onClick={() => removeFilter(key)}
                            className="ml-1 text-blue-600 hover:text-red-600 font-bold"
                          >
                            ×
                          </button>
                        </span>
                      )
                  )}

                  <button onClick={handleReset} className="block mt-2 text-xs text-red-600">
                    Reset Filters
                  </button>
                </div>

                <h4 className="mt-4 mb-2 font-semibold">Location</h4>
                {locations.map((loc) => (
                  <label key={loc.name} className="block text-sm">
                    <input
                      type="radio"
                      checked={selectedFilters.location === loc.name}
                      onChange={() =>
                        setSelectedFilters((p) => ({ ...p, location: loc.name }))
                      }
                    />{' '}
                    {loc.name}
                  </label>
                ))}

                <h4 className="mt-4 mb-2 font-semibold">Experience</h4>
                <div className="flex flex-wrap gap-2">
                  {experienceYears.map((y) => (
                    <div
                      key={y}
                      onClick={() =>
                        setSelectedFilters((p) => ({ ...p, experience: y }))
                      }
                      className={`w-8 h-8 flex items-center justify-center text-sm rounded-full border cursor-pointer ${selectedFilters.experience === y
                        ? 'bg-blue-600 text-white'
                        : ''
                        }`}
                    >
                      {y}
                    </div>
                  ))}
                </div>
              </div>
            </aside>

            {/* JOB LIST + PREVIEW */}
            <section className="w-full md:w-3/4 grid grid-cols-1 md:grid-cols-2 gap-6">


              {/* LEFT — JOB LIST */}
              <div className="space-y-4 md:overflow-y-auto md:max-h-[75vh] pr-2">




                <p className="mb-2 text-sm text-gray-600">
                  Showing {jobs.length} jobs
                  {selectedFilters.location ? ` near ${selectedFilters.location}` : ''}
                </p>

                {jobs.map((job) => {
                  const isActive = selectedJob?._id === job._id;

                  return (
                    <div
                      key={job._id}
                      onClick={() => setSelectedJob(job)}
                      className={`p-4 border rounded-lg cursor-pointer transition
            ${isActive
                          ? "border-blue-600 bg-blue-50 shadow-md ring-1 ring-blue-200"
                          : "bg-white hover:shadow-md"}

          `}
                    >
                      {/* Institute */}
                      <h3 className="text-sm font-semibold text-gray-900">
                        {job.Institution_id?.name || "Renowned School"}
                      </h3>

                      {/* Title */}
                      <p className="mt-1 text-sm font-semibold text-blue-600">
                        {job.title}
                      </p>

                      {/* Meta info */}
                      <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-600">
                        <span>📍 {job.address?.city || "Location not specified"}</span>

                        <span>
                          🕒{" "}
                          {job.max_experience
                            ? `${job.min_experience} – ${job.max_experience} Years`
                            : `${job.min_experience}+ Years`}
                        </span>


                        <span>
                          💼 {job.employment_type || "Not specified"}
                        </span>
                      </div>

                      <span className="text-xs text-gray-400">
                        {formatPostedTime(job.posted_date)}
                      </span>
                      {/* MOBILE ACTION BUTTON */}
                      <div className="mt-3 block md:hidden">
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // prevent card selection
                            navigate(`/job-description/${job._id}`);
                          }}
                          className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                        >
                          View Details & Apply
                        </button>
                      </div>

                    </div>
                  );
                })}
              </div>

              {/* RIGHT — JOB PREVIEW PANEL */}
              <div className="hidden md:block md:sticky md:top-24 md:max-h-[75vh] overflow-y-auto bg-white border rounded-xl shadow p-6">


                {selectedJob ? (
                  <>
                    {/* HEADER */}
                    <div className="border-b pb-4 mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {selectedJob.title}
                      </h3>

                      <p className="mt-1 text-sm text-gray-700 flex items-center gap-2">
                        <Building className="w-4 h-4 text-gray-500" />
                        {selectedJob.Institution_id?.name || "Renowned School"}
                      </p>
                    </div>

                    {/* INFO GRID (LIKE REFERENCE) */}
                    <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm text-gray-700">

                      {/* Location */}
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-yellow-500" />
                        <span>{selectedJob.address?.city || "Location not specified"}</span>
                      </div>

                      {/* Experience */}
                      <div className="flex items-center gap-2">
                        🕒
                        <span>
                          {selectedJob.max_experience
                            ? `${selectedJob.min_experience} – ${selectedJob.max_experience} Years`
                            : `${selectedJob.min_experience}+ Years`}
                        </span>

                      </div>

                      {/* Qualification */}
                      <div className="flex items-center gap-2">
                        🎓
                        <span>
                          {selectedJob.prefered_qualifications?.length
                            ? selectedJob.prefered_qualifications.join(", ")
                            : "Any Qualification"}
                        </span>
                      </div>

                      {/* Notice Period */}
                      <div className="flex items-center gap-2">
                        ⏳
                        <span>
                          {selectedJob.notice_period
                            ? `Joining in ${selectedJob.notice_period} Days`
                            : "Immediate Joining"}
                        </span>
                      </div>

                      {/* Employment Type */}
                      <div className="flex items-center gap-2">
                        💼
                        <span>{selectedJob.employment_type || "Not specified"}</span>
                      </div>

                      {/* Vacancies */}
                      <div className="flex items-center gap-2">
                        🏷️
                        <span>{selectedJob.vacancies || 1} Vacancies</span>
                      </div>
                    </div>




                    {/* APPLICANTS + POSTED INFO */}
                    <div className="flex justify-between text-xs text-gray-500 mt-3 border-b pb-3">

                      <span>
                        {selectedJob.applicants?.length || 0} Applicants
                      </span>

                      <span>
                        {formatPostedTime(selectedJob.posted_date)}
                      </span>

                    </div>


                    {/* DESCRIPTION */}
                    <div className="mt-6">
                      <h4 className="font-semibold text-gray-800 mb-2">Description</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {selectedJob.description ||
                          "As a teacher, you will engage students in innovative learning methods and contribute to academic excellence while supporting the institution’s mission."}
                      </p>
                    </div>

                    {/* ACTION BUTTONS */}
                    <div className="flex gap-4 mt-6">
                      <button
                        onClick={() => navigate(`/job-description/${selectedJob._id}`)}
                        className="flex-1 px-4 py-2 text-white bg-blue-700 rounded-md hover:bg-blue-800"
                      >
                        Description
                      </button>

                      <button
                        disabled={appliedJobIds.has(selectedJob._id)}
                        onClick={() => navigate(`/job-description/${selectedJob._id}`)}
                        className={`flex-1 px-4 py-2 rounded-md ${appliedJobIds.has(selectedJob._id)
                          ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                          : "border border-blue-700 text-blue-700 hover:bg-blue-50"
                          }`}
                      >
                        {appliedJobIds.has(selectedJob._id) ? "Already Applied" : "Apply"}
                      </button>

                    </div>
                  </>
                ) : (
                  <p className="text-gray-500">Select a job to view details</p>
                )}
              </div>

            </section>

          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default TeacherJobsPage;
